import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Named, Uuid } from "@shared/api";
import { Popover, PopoverAnchor, PopoverContent } from "@shared/ui/popover";
import {
  IconAt,
  IconClose,
  IconFilters,
  IconLink,
  IconSearch,
  IconTextChannel,
  IconTrash,
  IconUser,
} from "@shared/ui/icons";
import { dropKey, matchPeople, pendingKey, takeTokens, type Has, type TokenKey } from "../lib/search-tokens";
import { filterCount, filtersOf, useSearch } from "../model/search";
import { FiltersDialog, HAS_LABEL } from "./FiltersDialog";
import { overlaid } from "@shared/ui/hotkey";

/**
 * Поле поиска в шапке — как в Discord. Под полем при фокусе — меню: «от кого»,
 * «в каком канале», «что есть», «кого упоминают», «больше фильтров» (окно
 * «Фильтры») и недавние запросы. Слова-фильтры можно набрать и прямо в
 * строке — законченное встаёт в поле плашкой. Enter запускает поиск, находки —
 * колонкой справа (`onStart`); крестик стирает поиск и закрывает её (`onStop`).
 */
export function SearchField({
  placeholder,
  people,
  channels,
  onStart,
  onStop,
}: {
  placeholder: string;
  people: Map<Uuid, Named>;
  /** Текстовые каналы сервера — для «в:»; в переписке пусто. */
  channels: { id: Uuid; name: string }[];
  onStart: () => void;
  onStop: () => void;
}) {
  const search = useSearch();
  const [open, setOpen] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  // Ctrl+F — в поиск, как в Discord.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || event.code !== "KeyF") return;
      if (overlaid(event)) return;
      event.preventDefault();
      field.current?.focus();
      field.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const authors = useMemo(
    () => [...people.values()].sort((a, b) => (a.display_name || a.username).localeCompare(b.display_name || b.username)),
    [people],
  );
  const nameOf = (id: Uuid) => {
    const person = people.get(id);
    return person ? person.display_name || person.username : "кто-то";
  };

  const pending = pendingKey(search.text);
  const filters = filtersOf(search);
  const active = search.query !== null || !!search.text || filterCount(filters) > 0;

  function type(next: string) {
    const taken = takeTokens(next, authors, channels);
    const patch: Parameters<typeof search.patch>[0] = {};
    if (taken.author && !search.authors.includes(taken.author)) patch.authors = [...search.authors, taken.author];
    if (taken.mention && !search.mentions.includes(taken.mention)) patch.mentions = [...search.mentions, taken.mention];
    if (taken.channel && !search.channels.includes(taken.channel)) patch.channels = [...search.channels, taken.channel];
    if (taken.has) patch.has = taken.has;
    if (taken.after) patch.after = taken.after;
    if (taken.before) patch.before = taken.before;
    search.patch(patch);
    search.setText(taken.text);
  }

  /** Выбрали из подсказки — фильтр в плашку, слово из строки. */
  function choose(key: TokenKey, value: string) {
    if (key === "from") search.patch({ authors: [...new Set([...search.authors, value])] });
    else if (key === "mentions") search.patch({ mentions: [...new Set([...search.mentions, value])] });
    else if (key === "in") search.patch({ channels: [...new Set([...search.channels, value])] });
    else search.patch({ has: value as Has });
    search.setText(dropKey(search.text));
    field.current?.focus();
  }

  /** Вставить начало фильтра в строку — дальше подскажет меню. */
  function begin(token: string) {
    const text = search.text.trim();
    search.setText(`${text ? `${text} ` : ""}${token}`);
    field.current?.focus();
  }

  function start(text?: string) {
    if (text === undefined && !search.text.trim() && filterCount(filtersOf(useSearch.getState())) === 0) return;
    search.submit(text);
    setOpen(false);
    field.current?.blur();
    onStart();
  }

  function stop() {
    search.clear();
    setOpen(false);
    onStop();
  }

  // Подсказки к незаконченному слову-фильтру: люди, каналы или вид содержимого.
  const suggestions: { key: TokenKey; value: string; label: string; sub?: string; icon: ReactNode }[] =
    pending === null
      ? []
      : pending.key === "from" || pending.key === "mentions"
        ? matchPeople(authors, pending.needle).map((person) => ({
            key: pending.key,
            value: person.id,
            label: person.display_name || person.username,
            sub: person.handle,
            icon: <IconUser size={16} />,
          }))
        : pending.key === "in"
          ? channels
              .filter((channel) => channel.name.toLowerCase().includes(pending.needle.replace(/^#/u, "")))
              .slice(0, 8)
              .map((channel) => ({ key: "in" as const, value: channel.id, label: channel.name, icon: <IconTextChannel size={16} /> }))
          : (Object.keys(HAS_LABEL) as Has[])
              .filter((value) => HAS_LABEL[value].startsWith(pending.needle) || value.startsWith(pending.needle))
              .map((value) => ({ key: "has" as const, value, label: HAS_LABEL[value], icon: <IconLink size={16} /> }));

  const chip = (label: string, onRemove: () => void, key: string) => (
    <span key={key} className="head-search-chip">
      <span className="ellipsis">{label}</span>
      <button
        type="button"
        aria-label={`Убрать: ${label}`}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
      >
        <IconClose size={10} />
      </button>
    </span>
  );
  const chips = [
    ...search.authors.map((id) => chip(`от: ${nameOf(id)}`, () => search.patch({ authors: search.authors.filter((item) => item !== id) }), `a${id}`)),
    ...search.channels.map((id) =>
      chip(`в: #${channels.find((item) => item.id === id)?.name ?? "канал"}`, () => search.patch({ channels: search.channels.filter((item) => item !== id) }), `c${id}`),
    ),
    ...(search.has ? [chip(`имеет: ${HAS_LABEL[search.has]}`, () => search.patch({ has: null }), "has")] : []),
    ...(search.thread ? [chip(`в ветке: ${search.thread.name}`, () => search.patch({ thread: null }), "thread")] : []),
    ...search.mentions.map((id) =>
      chip(`упоминает: ${nameOf(id)}`, () => search.patch({ mentions: search.mentions.filter((item) => item !== id) }), `m${id}`),
    ),
  ];
  // Даты, тип автора и «закреплено» — одной плашкой: правят их в окне «Фильтры».
  const rest =
    (search.after ? 1 : 0) + (search.before ? 1 : 0) + (search.pinned !== null ? 1 : 0) + (search.authorType ? 1 : 0);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className={`head-search ${active ? "active" : ""}`} onClick={() => field.current?.focus()}>
            {chips}
            {rest > 0 &&
              chip(
                `ещё фильтров: ${rest}`,
                () => search.patch({ after: null, before: null, pinned: null, authorType: null }),
                "rest",
              )}
            <input
              ref={field}
              className="head-search-input"
              value={search.text}
              placeholder={chips.length > 0 || rest > 0 ? "" : placeholder}
              aria-label={placeholder}
              onFocus={() => setOpen(true)}
              onChange={(event) => {
                type(event.target.value);
                setOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setOpen(false);
                  field.current?.blur();
                } else if (event.key === "Enter") {
                  event.preventDefault();
                  if (suggestions[0]) choose(suggestions[0].key, suggestions[0].value);
                  else start();
                } else if (event.key === "Backspace" && !search.text) {
                  // Пустое поле — Backspace снимает последнюю плашку, как в Discord.
                  if (search.mentions.length) search.patch({ mentions: search.mentions.slice(0, -1) });
                  else if (search.has) search.patch({ has: null });
                  else if (search.channels.length) search.patch({ channels: search.channels.slice(0, -1) });
                  else if (search.authors.length) search.patch({ authors: search.authors.slice(0, -1) });
                }
              }}
            />
            {active ? (
              <button
                type="button"
                className="head-search-clear"
                aria-label="Стереть поиск"
                onClick={(event) => {
                  event.stopPropagation();
                  stop();
                }}
              >
                <IconClose size={14} />
              </button>
            ) : (
              <IconSearch size={15} className="head-search-glass" />
            )}
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="end"
          sideOffset={10}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            // Щелчок по самому полю — не «мимо»: меню остаётся.
            if ((event.target as HTMLElement | null)?.closest?.(".head-search")) event.preventDefault();
          }}
        >
          <div className="head-search-menu surface-pop" onMouseDown={(event) => event.preventDefault()}>
            {suggestions.length > 0 ? (
              <>
                <div className="head-search-title">
                  {pending?.key === "in" ? "Каналы" : pending?.key === "has" ? "Что есть в сообщении" : "Люди"}
                </div>
                {suggestions.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className="head-search-item"
                    onClick={() => choose(item.key, item.value)}
                  >
                    {item.icon}
                    <span className="head-search-what">
                      <span className="ellipsis">{item.label}</span>
                      {item.sub && <span className="head-search-sub">{item.sub}</span>}
                    </span>
                  </button>
                ))}
              </>
            ) : (
              <>
                <div className="head-search-title">Фильтры</div>
                <MenuItem icon={<IconUser size={16} />} title="От конкретного пользователя" token="от:" hint="пользователя" onPick={() => begin("от:")} />
                {channels.length > 0 && (
                  <MenuItem icon={<IconTextChannel size={16} />} title="Поиск в определённом канале" token="в:" hint="канале" onPick={() => begin("в:")} />
                )}
                <MenuItem
                  icon={<IconLink size={16} />}
                  title="Поиск определённого типа данных"
                  token="имеет:"
                  hint="ссылку, вложение или картинку"
                  onPick={() => begin("имеет:")}
                />
                <MenuItem
                  icon={<IconAt size={16} />}
                  title="Поиск упоминаний конкретного пользователя"
                  token="с упоминанием:"
                  hint="пользователя"
                  onPick={() => begin("упоминание:")}
                />
                <button
                  type="button"
                  className="head-search-item"
                  onClick={() => {
                    setOpen(false);
                    search.setEditing(true);
                  }}
                >
                  <IconFilters size={16} />
                  <span className="head-search-what">
                    Больше фильтров
                    <span className="head-search-sub">даты, тип автора и другие фильтры</span>
                  </span>
                </button>

                {search.history.length > 0 && (
                  <>
                    <div className="head-search-title with-action">
                      История
                      <button type="button" aria-label="Очистить историю" onClick={search.forget}>
                        <IconTrash size={14} />
                      </button>
                    </div>
                    {search.history.map((item) => (
                      <button key={item} type="button" className="head-search-item" onClick={() => start(item)}>
                        <IconSearch size={16} />
                        <span className="head-search-what ellipsis">{item}</span>
                      </button>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {search.editing && (
        <FiltersDialog
          people={authors}
          channels={channels}
          initial={filters}
          onClose={() => search.setEditing(false)}
          onApply={(next) => {
            search.patch(next);
            search.setEditing(false);
            start(search.text);
          }}
        />
      )}
    </>
  );
}

function MenuItem({
  icon,
  title,
  token,
  hint,
  onPick,
}: {
  icon: ReactNode;
  title: string;
  token: string;
  hint: string;
  onPick: () => void;
}) {
  return (
    <button type="button" className="head-search-item" onClick={onPick}>
      {icon}
      <span className="head-search-what">
        {title}
        <span className="head-search-sub">
          <b>{token}</b> {hint}
        </span>
      </span>
    </button>
  );
}
